import * as Sentry from '@sentry/react';
import { getCurrentPerson, getCurrentUser } from '@/db/services/user_service';
import { officialFullName } from '@/db/types/person';
import type { CurrentUserResponse, SelectedPerson } from '@/db/types/user';
import { createLogger, LogLevel } from './logger';

const logger = createLogger('SentryUser', LogLevel.INFO);

/**
 * The Sentry user for a signed-in staff member. `username` is what the
 * issue and replay lists display, so it carries the current person by name
 * (or "Person ID#… (email)" when the record has no name, or just the email
 * before an organization is selected). `id`/`email` stay the user account so
 * events can be searched with `user.id` / `user.email`; the person and
 * organization ids ride along as extra fields.
 */
export function buildSentryUser(
  user: CurrentUserResponse | null,
  person: SelectedPerson | null,
): Sentry.User | null {
  if (!user) return null;
  const base: Sentry.User = { id: String(user.id), email: user.email };
  if (!person) return { ...base, username: user.email };
  return {
    ...base,
    username: officialFullName(person) || `Person ID#${person.id} (${user.email})`,
    person_id: person.id,
    organization_id: person.organization?.id,
  };
}

const PERSON_TAG_KEYS = ['person_id', 'organization_id', 'organization'] as const;

/**
 * Indexed tags for the current person and organization. A user account is
 * not a person: one account can hold people in several organizations, and
 * the person/org is what places an event in the real world. Extra fields on
 * the Sentry user are display-only; tags can be searched (`person_id:170399`),
 * grouped and used in alerts. `undefined` clears a tag.
 */
export function buildSentryTags(person: SelectedPerson | null): Record<(typeof PERSON_TAG_KEYS)[number], string | undefined> {
  return {
    person_id: person ? String(person.id) : undefined,
    organization_id: person?.organization?.id !== undefined ? String(person.organization.id) : undefined,
    organization: person?.organization?.name || undefined,
  };
}

/** Sign-out: drop the user and the person/organization tags together. */
export function clearSentryUser(): void {
  Sentry.setUser(null);
  Sentry.setTags(buildSentryTags(null));
}

/**
 * Re-read the stored user and person and push them to Sentry. Call it whenever
 * either changes: after sign-in, organization selection, unlock and session
 * re-validation. Both records live in the encrypted database, so a call made
 * while the screen is locked (cold start) reads nothing — it then leaves the
 * current attribution untouched instead of clearing it; the unlock path calls
 * again once the key is available. Sign-out clears the user explicitly.
 */
export async function syncSentryUser(): Promise<void> {
  try {
    const [user, person] = await Promise.all([getCurrentUser(), getCurrentPerson()]);
    const sentryUser = buildSentryUser(user, person);
    if (!sentryUser) return;
    Sentry.setUser(sentryUser);
    Sentry.setTags(buildSentryTags(person));
  } catch (error) {
    logger.warn('Could not sync the Sentry user', error);
  }
}
